import type { Scene, SceneGroup } from '@lumen5/luminary';

import type { RootState } from 'js/src/libs/RootState';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isSceneAnOverlay,
    isVoiceoverSceneGroup,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import {
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
} from 'js/src/luminary/voicedriven-scenes';

/**
 * One scene's slice of its scene group's transcript: the transcript-time span the scene plays
 * over, the words inside that span, and the scene's role.
 *
 * - `overlay`: an overlay/cutaway scene layered on top of the waveform/talking-head base.
 * - `uncovered`: a base scene in a voiceover group — a coverage gap where the waveform is visible.
 * - `speaker`: a base scene in a talking-head / AI-avatar group — the speaker is on screen, which
 *   is the normal, intended state (not a gap).
 */
export type ScenePartition = {
    scene: Scene;
    startMillis: number; // transcript-time
    endMillis: number; // transcript-time
    words: string[];
    role: 'overlay' | 'uncovered' | 'speaker';
};

/**
 * Total, non-overlapping, timeline-ordered partition of a voice-driven scene group's transcript
 * across its scenes. In a voice-driven group scenes tile the timeline, so every transcript word
 * is owned by exactly one scene; assigning each word to its scene partitions the transcript.
 *
 * This is the single source of truth for "which transcript words does each scene own" — the
 * scene-coverage serialization, the coverage-gap report, and the overlay-collision check all
 * derive from it, so the word↔scene time math lives in exactly one place.
 *
 * Returns null when there is no transcript to partition (non-voicedriven / text-on-media groups).
 */
export function partitionTranscriptByScene(
    state: RootState,
    sceneGroup: SceneGroup,
): ScenePartition[] | null {
    const transcript = sceneGroup.originalTranscript;
    const transcriptWords = transcript?.transcriptData?.words || transcript?.words || [];
    if (transcriptWords.length === 0) {
        return null;
    }

    // A base (non-overlay) scene is an uncovered gap only in a voiceover group where the waveform
    // is otherwise visible. In talking-head / AI-avatar groups the base scene shows the speaker,
    // which is the intended state rather than a gap.
    const baseRole: 'uncovered' | 'speaker' =
        isVoiceoverSceneGroup(sceneGroup) && !isAIAvatarSceneGroup(sceneGroup)
            ? 'uncovered'
            : 'speaker';

    const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
    const baseAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

    return sceneGroup.scenes.map((scene) => {
        const startMillis = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneStartTime(scene),
            playbackRegions,
            baseAssetSourceDuration,
            sceneGroup.startTime,
        );
        const endMillis = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneEndTime(scene),
            playbackRegions,
            baseAssetSourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: false },
        );
        const words = transcriptWords
            .filter((word) => !(word.endTime <= startMillis || word.startTime >= endMillis))
            .map((word) => word.word);
        const role = isSceneAnOverlay(state, scene) ? 'overlay' : baseRole;
        return { scene, startMillis, endMillis, words, role };
    });
}
